import EndpointsHandler from "@/utils/endpointsHandler";
import AxiosConnect from "@/common/api/AxiosConnect";
import canUseNewRpa from "@/utils/canUseNewRpa";

export default {
  namespaced: true,
  state: () => ({
    dataAxiosLocal: {
      baseURL: process.env.VUE_APP_AUTLOCAL_API,
      timeout: 60000,
    },
    totalPercentaje: 0,
    intervalPercentaje: null,
    tryToOpenCounter: 0,
    userProfile: {},
    endpointsHandler: null,
    rooms: [],
  }),
  mutations: {
    setNewTry(state) {
      state.tryToOpenCounter++;
    },
    resetTryCounter(state) {
      state.tryToOpenCounter = 0;
    },
    setProcessId(state, processId) {
      state.processId = processId;
    },
    setUserProfile(state, { profile, endpointsHandler }) {
      state.userProfile = profile;
      state.endpointsHandler = endpointsHandler;
    },
  },
  actions: {
    syncUserProfile({ commit, rootState }, userProfile) {
      const localApi = process.env.VUE_APP_AUTLOCAL_API || "";
      const localApiV2 = process.env.VUE_APP_AUTLOCALV2_API || localApi;

      const endpointsHandler = new EndpointsHandler(localApi, localApiV2, () =>
        canUseNewRpa({
          rootState,
          targetUser: userProfile,
        })
      );

      commit("setUserProfile", { profile: userProfile, endpointsHandler });
    },
    async authJasminCam({ state, dispatch, commit, rootState }, user) {
      try {
        this.$alerts.dataSending({
          title: "Abriendo Jasmin",
          message: "Se está abriendo la aplicación, espere un momento, por favor.",
        });
        dispatch("setProgress");

        const { jasminUser, jasminPassword, ips } = user;
        const openApp = await dispatch("openJasminCam", user);

        if (!openApp) throw new Error("No se abrió la aplicación, intenté nuevamente.");

        const processId = await dispatch("getIdProcess");
        if (!processId) throw new Error("No se encontró el proceso requerido.");

        const canUseNewRpaValue = canUseNewRpa({ rootState, targetUser: user });

        if (canUseNewRpaValue) {
          const rpaBody = { username: jasminUser, password: jasminPassword, handle: processId };
          await dispatch("openJasminCamPlatform", rpaBody);
          return;
        }

        const isValidIps = Array.isArray(ips) && ips.length > 0;
        const body = {
          params: {
            param1: processId,
            param2: jasminUser,
            param3: jasminPassword,
            param4: isValidIps ? ips[0] : null,
          },
        };
        await state.endpointsHandler.execute("/run-auth", "POST", body);

        this.$alerts.onChangeProgress(100);
        this.$alerts.closeSwal();
      } catch (error) {
        this.$alerts.closeSwal();
        this.$alerts.dataSendError({ message: error.message });
        console.log(error);
      } finally {
        clearInterval(state.intervalPercentaje);
        commit("setNewTry");
        state.totalPercentaje = 0;
      }
    },
    async authModelcenter({ state, dispatch, commit }, user) {
      try {
        this.$alerts.dataSending({
          title: "Abriendo el modelcenter",
          message: "Se está abriendo la aplicación, espere un momento, por favor.",
        });
        dispatch("setProgress");

        const { jasminUser, jasminPassword } = user;
        const body = { params: { param1: jasminUser, param2: jasminPassword } };
        await state.endpointsHandler.execute("/run-auth/modelcenterjcam", "POST", body);

        this.$alerts.onChangeProgress(100);
        this.$alerts.closeSwal();
      } catch (error) {
        this.$alerts.closeSwal();
        this.$alerts.dataSendError({ message: error.message });
        console.log(error);
      } finally {
        clearInterval(state.intervalPercentaje);
        commit("setNewTry");
        state.totalPercentaje = 0;
      }
    },
    async authStreamate({ state, dispatch, commit }, user) {
      try {
        this.$alerts.dataSending({
          title: "Abriendo Streamate",
          message: "Se está abriendo la aplicación, espere un momento, por favor.",
        });
        dispatch("setProgress");

        const { streamateUser, streamatePassword } = user;
        const body = { params: { param1: streamateUser, param2: streamatePassword } };
        await state.endpointsHandler.execute("/run-auth/streamate", "POST", body);

        this.$alerts.onChangeProgress(100);
        this.$alerts.closeSwal();
      } catch (error) {
        console.log(error);
      } finally {
        clearInterval(state.intervalPercentaje);
        commit("setNewTry");
        state.totalPercentaje = 0;
      }
    },
    async openJasminCam({ state }) {
      try {
        await state.endpointsHandler.execute("/closeapp", "GET", {});
        const { data } = await state.endpointsHandler.execute("/openapp", "GET", {});
        const { isRunning = false } = data;

        return isRunning;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    async getIdProcess({ state, commit }) {
      try {
        const { data } = await state.endpointsHandler.execute("/idwindows", "GET", {});
        const { handle = null } = data;
        commit("setProcessId", handle);
        return handle;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    setProgress({ state }) {
      const timer = 550;
      state.intervalPercentaje = setInterval(() => {
        if (state.totalPercentaje < 99) {
          state.totalPercentaje += 1;
          this.$alerts.onChangeProgress(state.totalPercentaje);
        }
      }, timer);
    },
    async executeEndpoint({ state, dispatch, commit }, { title = "Abriendo", message = "Espere un momento, por favor.", path = "/", method = "POST", body = {} } = {}) {
      try {
        this.$alerts.dataSending({ title, message });
        dispatch("setProgress");

        const res = await state.endpointsHandler.execute(path, method, body);

        this.$alerts.onChangeProgress(100);
        this.$alerts.closeSwal();

        return res;
      } catch (error) {
        this.$alerts.closeSwal();
        if (this.$alerts?.dataSendError) this.$alerts.dataSendError({ message: error.message });
        console.log(error);
        throw error;
      } finally {
        clearInterval(state.intervalPercentaje);
        commit("setNewTry");

        state.totalPercentaje = 0;
      }
    },

    async openPlatform({ dispatch }, body) {
      return dispatch("executeEndpoint", {
        title: "Abriendo plataforma",
        message: "Se está abriendo la plataforma, espere un momento, por favor.",
        path: "/run-auth",
        method: "POST",
        body,
      });
    },
    async openJasminCamPlatform({ dispatch }, body) {
      return dispatch("executeEndpoint", {
        title: "Abriendo Jasmin Cam",
        message: "Se está abriendo la plataforma, espere un momento, por favor.",
        path: "/run-auth/desktop-login",
        method: "POST",
        body,
      });
    },
    async closePlatform({ state }, platform) {
      try {
        const { data = {} } = await state.endpointsHandler.execute(`/stop/${platform}`, "GET", {});
        const { sucess = false } = data;

        return sucess;
      } catch (error) {
        console.log(error);
        return;
      }
    },
    async getPasswordChangeHistories() {
      const axiosConfig = {
        baseURL: process.env.VUE_APP_API_IP,
        timeout: 60000,
        tokenName: process.env.VUE_APP_TOKEN_NAME,
      };
      const res = await AxiosConnect.instance(axiosConfig).request({
        url: "/getpasswordChangeHistories?status=running",
        method: "GET",
      });
      return res.data;
    },

    async getCompletedPasswordChanges({}, { page = 0, pageSize = 10, platform = null } = {}) {
      const axiosConfig = {
        baseURL: process.env.VUE_APP_API_IP,
        timeout: 60000,
        tokenName: process.env.VUE_APP_TOKEN_NAME,
      };
      const params = { status: "all", page, pageSize };
      if (platform) params.platform = platform;
      const res = await AxiosConnect.instance(axiosConfig).request({
        url: "/getpasswordChangeHistories",
        method: "GET",
        params,
      });
      return res.data;
    },

    async executeChangePassword({ state, dispatch, commit }, { userId, platform, ids, locations }) {
      try {
        const body = {
          userId,
          platform,
          ids,
          locations,
        };

        const axiosConfig = {
          baseURL: process.env.VUE_APP_CHANGE_PASSWORD_API,
          timeout: 60000,
          tokenName: process.env.VUE_APP_TOKEN_NAME,
        };
        const res = await AxiosConnect.instance(axiosConfig).request({
          url: "/usuarios",
          method: "POST",
          data: body,
        });

        this.$alerts.closeSwal();

        return res;
      } catch (error) {
        this.$alerts.closeSwal();
        if (this.$alerts?.dataSendError) this.$alerts.dataSendError({ message: error.message });
        console.log(error);
        throw error;
      } finally {
        clearInterval(state.intervalPercentaje);
        commit("setNewTry");
        state.totalPercentaje = 0;
      }
    },

    async executePlatformAction({ dispatch, rootState }, { platform, targetUser = {}, preferAuthJasminCam = false, forceUseNewRpa = null } = {}) {
      if (!platform) return;

      const platformName = platform?.platformId?.name || platform;
      const isChaturbatePlatform = platformName === "Chaturbate";

      const rpaParams = {
        platform: platformName,
        username: isChaturbatePlatform ? platform.userName : platform.email,
        password: platform.password,
        tipeUser: "Modelo",
      };

      const credentials = {
        streamateUser: targetUser.streamateUser,
        streamatePassword: targetUser.streamatePassword,
        jasminUser: targetUser.jasminUser,
        jasminPassword: targetUser.jasminPassword,
        room: targetUser.room,
        ips: targetUser.ips,
        platformType: targetUser.platformType,
      };

      const canUseNewRpaValue = canUseNewRpa({ forceUseNewRpa, rootState, targetUser });

      const platformActions = {
        LiveJasmin: {
          newRpa: () => (!preferAuthJasminCam ? dispatch("openPlatform", rpaParams) : dispatch("authJasminCam", credentials)),

          default: () => (preferAuthJasminCam ? dispatch("authJasminCam", credentials) : dispatch("authModelcenter", credentials)),
        },
        Streamate: {
          newRpa: () => dispatch("openPlatform", rpaParams),
          default: () => dispatch("authStreamate", credentials),
        },
        Stripchat: { newRpa: () => dispatch("openPlatform", rpaParams) },
        Chaturbate: { newRpa: () => dispatch("openPlatform", rpaParams) },
      };

      const handlers = platformActions[platformName];
      if (!handlers) {
        console.warn("Plataforma no soportada:", platformName);
        return;
      }

      const action = canUseNewRpaValue ? handlers.newRpa : handlers.default;
      if (typeof action === "function") return action();
    },
  },
  getters: {
    canAttemptOpen(state) {
      const now = new Date();
      const colombiaOffset = -5 * 60;
      const colombiaTime = new Date(now.getTime() + (now.getTimezoneOffset() + colombiaOffset) * 60 * 1000);
      const currentDay = colombiaTime.getUTCDay();
      const currentHour = colombiaTime.getHours();

      const isWithinSchedule = (currentDay > 1 || (currentDay === 1 && currentHour >= 5)) && (currentDay < 6 || (currentDay === 6 && currentHour < 5));

      return state.tryToOpenCounter >= 2 && isWithinSchedule;
    },
    orderedPlatforms: () => (credentials = []) => {
      const filtered = (credentials || []).filter((cred) => Boolean(cred.password));
      const platformOrder = ["LiveJasmin", "Streamate", "Chaturbate", "Stripchat"];

      return filtered.sort((a, b) => {
        const aName = a.platformId?.name ?? "";
        const bName = b.platformId?.name ?? "";

        const aIndex = platformOrder.indexOf(aName);
        const bIndex = platformOrder.indexOf(bName);

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      });
    },
  },
};
